/**
 * basemapImagery.ts — مصدر الصور الجوّية تحت الرادار.
 *
 * ── لماذا مصدران لا واحد ──────────────────────────────────────────────────────
 *
 * الشكل المطلوب (‏WeatherFront، وتوقيعُ خريطتهم يقول `mapbox`) يحتاج صوراً بدقّة
 * ~0.3 متر حتّى تكبير 19. وهذه **لا توجد مجّاناً**: الصور بهذه الدقّة مِلك شركات
 * تصوير، ولا أحد يوزّعها بلا مقابل. قِيس ذلك لا خُمِّن — اختُبرت ثلاثة مصادر فوق
 * نيوارك:
 *
 *   USGS NAIP        z12 ✓  z15 ✓  z17 ✗        ~1 متر،  **ملك عام**
 *   Esri World Img   z12 ✓  z15 ✓  z19 ✓        ~0.3 م،  يحتاج اشتراك ArcGIS
 *   EOX Sentinel-2   z12 ✓  z15 ✓  z18 ✓        10 أمتار، CC BY 4.0
 *   Mapbox Satellite z19+ ✓                      ~0.3 م،  مفتاح + تسعيرة
 *
 * فالمفتاح يُقرأ من البيئة، وعند غيابه نسقط على المجّاني بدل أن تُعطَب الطبقة.
 * والسقوط ليس تنازلاً صامتاً: `imagerySourceLabel()` تقول أيّهما يعمل، وتظهر في
 * إسناد الخريطة.
 *
 * ── ولماذا المفتاح في البيئة لا في الكود ─────────────────────────────────────
 *
 * مفتاح Mapbox العامّ (`pk.…`) يُنشر في حزمة الواجهة بحكم التصميم، لكنه يظلّ
 * **مربوطاً بحسابٍ يُحاسَب بالاستعمال**. وضعُه في مستودع Git يعني أن كل نسخة من
 * التاريخ تحمله إلى الأبد، وأن سحبه يستلزم تدوير المفتاح لا حذف سطر. البيئة تُبقيه
 * خارج التاريخ، وتسمح بمفتاحٍ مختلف للتطوير والإنتاج.
 */

const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined)?.trim() || '';

/** أنماط Mapbox المتاحة — `satellite-streets` هو ما تستعمله WeatherFront. */
const MAPBOX_TILES = (token: string) =>
    `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${token}`;

/**
 * ‏USGS National Map — صور NAIP الجوّية، مِلك عام أمريكي بلا مفتاح ولا قيد تجاري.
 *
 * ‏maxzoom = 16 ليس اختياراً بل حدّ الخدمة المقيس: ما فوقه يردّ 404. وضبطه صراحةً
 * يجعل MapLibre **يُكبّر آخر بلاطة متاحة** بدل أن يطلب بلاطاتٍ غير موجودة — أي صورة
 * تنعم بدل فراغٍ رماديّ ومئات الطلبات الفاشلة.
 */
const USGS_TILES = 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}';

/** ‏Sentinel-2 cloudless من EOX — عالمي، ‏CC BY 4.0، حرّ تجارياً بالإسناد. */
const EOX_TILES = 'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg';

export interface ImagerySource {
    id: 'mapbox' | 'usgs' | 'eox';
    tiles: string[];
    tileSize: number;
    maxzoom: number;
    attribution: string;
    /** هل يغطّي العالم؟ ‏USGS أمريكيّ فقط. */
    global: boolean;
}

/**
 * هل عُطِّل Mapbox لهذه الجلسة بعد فشلٍ من جهة الحساب؟
 *
 * ── الاتفاق المتّفق عليه مع العميل ────────────────────────────────────────────
 * الباقة المجّانية 50 ألف طلب شهرياً. وعند تجاوزها **يعود التطبيق إلى الخلفية
 * الرمادية بلا أن يظهر للمستخدم أي خلل** — لا رسالة خطأ، ولا بلاطات ناقصة، ولا
 * شبكة رمادية مثقوبة. هذا العلَم هو ما يحقّق ذلك.
 *
 * وهو **لكل جلسة** لا دائم: الحصّة تُصفَّر شهرياً، وحفظُ التعطيل في التخزين المحلّي
 * كان سيُبقي المستخدم على الرمادي حتى بعد عودة الحصّة.
 */
let mapboxDisabled = false;

/** رموز الحالة التي تعني «الحساب لا الشبكة»: مفتاح خاطئ، حصّة، أو تقييد معدّل. */
const ACCOUNT_STATUSES = new Set([401, 402, 403, 429]);

/**
 * هل يستدعي هذا الفشلُ التخلّي عن Mapbox؟
 *
 * التمييز جوهري: انقطاعُ شبكةٍ عابر (‏0 أو 5xx) يُصلحه المتصفّح بإعادة الطلب، وإسقاطُ
 * الخلفية لأجله يعني خسارة الصور عند أوّل نفق. أمّا 402/429 فلا يُصلحها انتظار.
 */
export function isMapboxAccountFailure(status: number | undefined): boolean {
    return status !== undefined && ACCOUNT_STATUSES.has(status);
}

/** يُعطّل Mapbox لهذه الجلسة. يُعيد true إن كان هذا أوّل تعطيل (فيلزم إعادة التنسيق). */
export function disableMapbox(): boolean {
    if (mapboxDisabled) return false;
    mapboxDisabled = true;
    return true;
}

export function isMapboxDisabled(): boolean {
    return mapboxDisabled;
}

export function hasMapboxToken(): boolean {
    return MAPBOX_TOKEN.length > 0;
}

/**
 * المصدر الفعّال.
 *
 * عند وجود المفتاح: Mapbox وحده — عالمي وبأعلى دقّة، فلا داعي لخلطه بغيره.
 * عند غيابه: نجمع الاثنين المجّانيين. ‏USGS فوق (‏1 متر، أمريكا) وSentinel تحته
 * (‏10 أمتار، العالم)؛ فحيث ينتهي الأمريكي يظهر العالمي بدل الفراغ. وهذا الترتيب
 * يخدم نطاق رادارنا بالذات: MRMS أمريكيّ، فأدقّ الصور حيث أدقّ البيانات.
 */
export function imagerySources(): ImagerySource[] {
    // نفدت الحصّة أو بطل المفتاح: **لا صور إطلاقاً**، لا سقوط على مصدر آخر.
    //
    // وهذا اتّفاق مع العميل لا قصور: السقوط على مصدر مجّاني كان سيبدّل شكل الخريطة
    // فجأةً أمام المستخدم (دقّة أخشن، ألوان مختلفة، تغطية أمريكية فقط) — وهو «ظهور
    // مشكلة» بالضبط ما اتُّفق على تجنّبه. الرمادي حالةٌ يعرفها المستخدم أصلاً.
    if (hasMapboxToken() && mapboxDisabled) return [];

    if (hasMapboxToken()) {
        return [{
            id: 'mapbox',
            tiles: [MAPBOX_TILES(MAPBOX_TOKEN)],
            // ‏512@2x: بلاطة واحدة تغطّي ما تغطّيه أربعٌ من 256، فربع عدد الطلبات
            // لنفس المساحة — وهو ما يُحاسَب عليه حساب Mapbox.
            tileSize: 512,
            maxzoom: 22,
            attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            global: true,
        }];
    }
    return [
        {
            id: 'eox',
            tiles: [EOX_TILES],
            tileSize: 256,
            maxzoom: 18,
            attribution: 'Sentinel-2 cloudless by <a href="https://s2maps.eu">EOX</a> (CC BY 4.0)',
            global: true,
        },
        {
            id: 'usgs',
            tiles: [USGS_TILES],
            tileSize: 256,
            maxzoom: 16,
            attribution: '<a href="https://basemap.nationalmap.gov/">USGS National Map</a> (public domain)',
            global: false,
        },
    ];
}

/** اسم المصدر العامل — يُعرض في الإسناد وفي سجلّ التطوير. */
export function imagerySourceLabel(): string {
    if (hasMapboxToken()) return mapboxDisabled ? 'Mapbox (معطَّل — نفدت الحصّة)' : 'Mapbox Satellite Streets';
    return 'USGS NAIP + Sentinel-2 cloudless';
}
